import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormGroup, FormBuilder, FormControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { LoggerService } from '../../../core/services/logger.service';
import { SessionService } from '../../../core/services/session.service';
import { Country } from '../../../core/models/country.model';
import { LoaderService } from '../../../core/services/loader.service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as _ from 'lodash';
import { CountrySelectConfirmationDialogComponent } from '../country-select-confirmation-dialgog/country-select-confirmation-dialog.component';

@Component({
	selector: 'cfe-edit-lab-availability',
	templateUrl: './country-select-dialog.component.html',
	styleUrls: ['./country-select-dialog.component.scss']
})
export class CountrySelectDialogComponent implements OnInit, OnDestroy {
	/**
	 * List of countries that should be preselected
	 */
	@Input() value: Country[];

	/**
	 * List of all available Countries
	 */
	@Input() availableCountries: Country[] = [];

	/**
	 * Current users country in billing address
	 */
	userCountry: Country;

	/**
	 * Form
	 */
	form: FormGroup;

	/**
	 * Flag to handle the disabled status of the apply button
	 */
	hasChanges = false;

	/**
	 * Object to render the form controls alphabetically in groups
	 */
	formStructure: {
		group: string;
		countries: Country[];
	}[];

	/**
	 * Determine if all countries are selected
	 */
	get areAllCountriesSelected(): boolean {
		return this.value.length === this.availableCountries.length;
	}

	/**
	 * Determine if only the land is selected where the user is located in
	 */
	get isOnlyUsersCountrySelected(): boolean {
		return this.value.length === 1;
	}

	/**
	 * Initial value
	 */
	private initialFormValue: { [code: string]: boolean };

	/**
	 * Subscriptions
	 */
	private subscriptions: Subscription = new Subscription();

	constructor(
		protected session: SessionService,
		protected activeModal: NgbActiveModal,
		protected formBuilder: FormBuilder,
		protected logger: LoggerService,
		protected loaderService: LoaderService,
		protected ngbModal: NgbModal
	) {}

	/**
	 * Angular lifecycle hook OnInit
	 */
	ngOnInit(): void {
		this.userCountry = this.session?.currentUser.company.billingAddress.country;
		this.initForm();
	}

	/**
	 * Angular lifecycle hook OnDestroy
	 */
	ngOnDestroy(): void {
		// Destroy all subscriptions
		this.subscriptions.unsubscribe();
	}

	selectAll(): void {
		const value = {};
		this.availableCountries.forEach((country: Country) => {
			value[country.code] = true;
		});
		this.form.reset(value);
	}

	clear(): void {
		const value = {
			[this.userCountry.code]: true
		};
		this.form.reset(value);
	}

	cancel(): void {
		this.form.reset(this.initialFormValue);
	}

	close(): void {
		this.activeModal.close();
	}

	apply(): void {
		this.showConfirmation().subscribe((result) => {
			if (result) {
				this.activeModal.close(this.value);
			} else {
				this.cancel();
			}
		});
	}

	initForm(): void {
		this.createFormStructure();
		this.form = this.formBuilder.group({});

		this.availableCountries.forEach((country: Country) => {
			const control = new FormControl();
			// Disable billing address when it is in available countries
			if (country.code === this.userCountry.code && this.value.find((item) => item.code === this.userCountry.code)) {
				control.setValue(true);
				control.disable();
			} else {
				if (this.value && this.value.find((item) => item.code === country.code)) {
					control.setValue(true);
				}
			}

			this.form.addControl(country.code, control);
		});

		this.initialFormValue = _.clone(this.form.getRawValue());

		this.subscriptions.add(
			this.form.valueChanges.subscribe(() => {
				this.value = this.getListFromFormValue(this.form.getRawValue());
				const initialValue = this.getListFromFormValue(this.initialFormValue);
				this.hasChanges = !_.isEqual(this.value, initialValue);

				// Disable billing address when it is selected
				if (this.form.getRawValue()[this.userCountry.code]) {
					if (this.form.get(this.userCountry.code).enabled) {
						this.form.get(this.userCountry.code).disable();
					}
				}
			})
		);
	}

	getCountryLabel(countryCode: string): string {
		return this.availableCountries.find((item: Country) => {
			return item.code === countryCode;
		}).name;
	}

	createFormStructure() {
		const groupedCountries = _.groupBy<Country>(this.availableCountries, (item: Country) => {
			return item.name.charAt(0);
		});
		this.formStructure = [];
		_.orderBy(Object.keys(groupedCountries)).forEach((key: string) => {
			this.formStructure.push({
				group: key,
				countries: _.orderBy(groupedCountries[key], (country) => country.name)
			});
		});
	}

	private getListFromFormValue(value: { [code: string]: boolean }): Country[] {
		const list = [];
		_.forEach(value, (value, countryCode) => {
			if (value) {
				const item = this.availableCountries.find((country: Country) => {
					return country.code === countryCode;
				});
				if (item) {
					list.push(item);
				}
			}
		});
		return list;
	}

	private showConfirmation(): Observable<boolean> {
		const initialValue = this.getListFromFormValue(this.initialFormValue);
		const countDeselected = _.differenceBy(initialValue, this.value, 'code').length;
		const countSelected = _.differenceBy(this.value, initialValue, 'code').length;

		const modalRef = this.ngbModal.open(CountrySelectConfirmationDialogComponent, {
			scrollable: true,
			windowClass: 'modal-country-select-confirmation modal-second-level',
			centered: true
		});

		modalRef.componentInstance.countSelected = countSelected;
		modalRef.componentInstance.countDeselected = countDeselected;

		return modalRef.closed;
	}
}
